import { TeamMembershipType } from 'types';

/**
 * The "Mosaic Support" account is a system-owned membership that lives on
 * every customer team. Bulk operations (role changes, activation, password
 * resets) should not touch it — the rails-api would either refuse the change
 * or lock support out of the team.
 *
 * rails-api reports it directly as `is_csm_account` (BE-12303), read only when
 * `galaxyCsmSupportAccountFlag` is on. Until then — and against any rails-api
 * that predates that field — we fall back to matching on name + email, which
 * requires BOTH:
 *  - name is exactly "Mosaic Support" (case-insensitive, trimmed)
 *  - email contains "mosaicapp.com"
 *
 * Both conditions must match so a customer who happens to type "Mosaic
 * Support" as a member's name (or any Mosaic employee on the team) is not
 * accidentally excluded from bulk operations.
 */
const SUPPORT_NAME = 'mosaic support';
const MOSAIC_EMAIL_FRAGMENT = 'mosaicapp.com';

type SupportAccountFields = Pick<TeamMembershipType, 'name' | 'email' | 'is_csm_account'>;

const matchesSupportNameAndEmail = (member: SupportAccountFields): boolean => {
  const name = (member.name || '').toLowerCase().trim();
  if (name !== SUPPORT_NAME) return false;

  const email = (member.email || '').toLowerCase();
  return email.includes(MOSAIC_EMAIL_FRAGMENT);
};

export const isMosaicSupportAccount = (
  member: SupportAccountFields,
  { useCsmAccountField = false }: { useCsmAccountField?: boolean } = {},
): boolean => {
  // Nullish, not falsy: an explicit `false` has to win, or a customer's own
  // member named "Mosaic Support" at a mosaicapp.com address stays wrongly
  // excluded from bulk operations. The absent case should not happen (the flag
  // is only enabled after rails-api ships the field) — it falls back to the
  // heuristic so enabling the flag early keeps the account protected.
  if (useCsmAccountField && member.is_csm_account != null) {
    return member.is_csm_account;
  }

  return matchesSupportNameAndEmail(member);
};
